import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Target, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { scenarioOptions, scenarioData } from './scenarioData';

// Risk Badge Component
const RiskBadge = ({ level }) => {
  const config = {
    low: { bg: 'bg-stable/20', text: 'text-stable', border: 'border-stable/30', label: 'Low Risk' },
    medium: { bg: 'bg-warning/20', text: 'text-warning', border: 'border-warning/30', label: 'Medium Risk' },
    high: { bg: 'bg-destructive/20', text: 'text-destructive', border: 'border-destructive/30', label: 'High Risk' },
  };
  const c = config[level] || config.medium;
  return (
    <Badge className={`${c.bg} ${c.text} ${c.border} border font-semibold`}>
      {c.label}
    </Badge>
  );
};

// Probability Icon
const ProbabilityIcon = ({ value }) => {
  const num = parseInt(value);
  if (num >= 30) return <TrendingUp className="w-4 h-4 text-stable" />;
  if (num >= 15) return <Minus className="w-4 h-4 text-warning" />;
  return <TrendingDown className="w-4 h-4 text-destructive" />;
};

export const ScenarioSelector = ({ selectedScenario, onScenarioChange }) => {
  const currentScenario = scenarioData[selectedScenario];
  
  return (
    <section className="mb-10" data-testid="scenario-selector-section">
      <div className="flex items-center gap-3 mb-6">
        <Target className="w-6 h-6 text-primary" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Scenario Selection</h2>
      </div>
      
      <Card className="bg-card border-border/50">
        <CardContent className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Dropdown Selector */}
            <div>
              <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-3">
                Tournament Outcome
              </label>
              <Select value={selectedScenario} onValueChange={onScenarioChange}>
                <SelectTrigger 
                  className="w-full bg-muted/30 border-border/50"
                  data-testid="scenario-dropdown"
                >
                  <SelectValue placeholder="Select scenario" />
                </SelectTrigger>
                <SelectContent>
                  {scenarioOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center justify-between w-full gap-4">
                        <span>{option.label}</span>
                        <span className="text-xs text-muted-foreground">({option.probability})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <p className="text-xs text-muted-foreground mt-3">
                Select tournament outcome to model financial impact
              </p>
            </div>
            
            {/* Scenario Summary */}
            <div className="p-4 rounded-lg bg-muted/20 border border-border/30">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ProbabilityIcon value={currentScenario.probability} />
                  <span className="text-sm font-medium text-foreground">
                    {currentScenario.label}
                  </span>
                </div>
                <RiskBadge level={currentScenario.riskLevel} />
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Probability</p>
                  <p className="text-lg font-bold text-foreground">{currentScenario.probability}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Brand Impact</p>
                  <p className={`text-lg font-bold ${currentScenario.brandValueIncrease >= 0 ? 'text-stable' : 'text-destructive'}`}>
                    {currentScenario.brandValueIncrease >= 0 ? '+' : ''}${currentScenario.brandValueIncrease}M
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default ScenarioSelector;
